import React from 'react';
import { Box, Typography, CircularProgress, styled } from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';


interface ScoreDistributionProps {
  distribution: {
    r1: number;  // レーティング -2.9
    r2: number;  // レーティング 3.0-3.9
    r3: number;  // レーティング 4.0-
    high_rate: number;
    neutral_rate: number;
    low_rate: number;
  };
  rating_jp?: number | null;
  totalCount?: number;
}

const DistributionBar = styled(Box)(({ theme }) => ({
  width: '100%',
  height: '6px',
  backgroundColor: 'rgba(255, 255, 255, 0.08)',
  overflow: 'hidden',
  position: 'relative',
  marginBottom: theme.spacing(2),
  borderRadius: '3px',
  [theme.breakpoints.down('sm')]: {
    height: '4px',
  },
}));

const ScoreSegment = styled(Box)<{ width: number; color: string }>(({ theme, width, color }) => ({
  position: 'absolute',
  height: '100%',
  width: `${width}%`,
  backgroundColor: color,
  transition: 'width 0.3s ease-in-out',
  opacity: width > 0 ? 0.9 : 0.3,
  '&::after': {
    content: '""',
    position: 'absolute',
    right: 0,
    top: 0,
    bottom: 0,
    width: '6px',
    backgroundColor: '#121212',
    zIndex: 1,
    display: width < 4 ? 'none' : 'block',
    [theme.breakpoints.down('sm')]: {
      width: '4px',
    },
  },
  '&:last-child::after': {
    display: 'none',
  },
}));

const LegendContainer = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(3, 1fr)',
  gap: theme.spacing(1),
  marginTop: theme.spacing(1.5),
}));

const LegendItem = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(0.25),
  backgroundColor: 'rgba(255, 255, 255, 0.05)',
  padding: '8px 16px',
  borderRadius: '4px',
  [theme.breakpoints.down('sm')]: {
    padding: '4px 8px',
  },
}));

const LegendLabel = styled(Typography)(({ theme }) => ({
  fontSize: '0.7rem',
  fontWeight: 400,
  color: theme.palette.text.secondary,
  display: 'flex',
  alignItems: 'center',
  [theme.breakpoints.down('sm')]: {
    fontSize: '0.65rem',
  },
}));

const LegendValue = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'baseline',
  gap: theme.spacing(1),
  [theme.breakpoints.down('sm')]: {
    gap: theme.spacing(0.75),
  },
}));

const LegendPercentage = styled(Typography)(({ theme }) => ({
  fontSize: '1rem',
  fontWeight: 600,
  color: '#ffffff',
  [theme.breakpoints.down('sm')]: {
    fontSize: '0.8125rem',
  },
}));

const LegendCount = styled(Typography)(({ theme }) => ({
  fontSize: '0.6rem',
  opacity: 0.75,
  color: theme.palette.text.secondary,
  [theme.breakpoints.down('sm')]: {
    fontSize: '0.6rem',
  },
}));

const RatingContainer = styled(Box)(({ theme, isMobile }: { theme: any; isMobile?: boolean }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: isMobile ? '4px' : '8px',
  padding: isMobile ? '4px' : '12px',
  backgroundColor: 'rgba(255, 255, 255, 0.05)',
  backdropFilter: 'blur(4px)',
  WebkitBackdropFilter: 'blur(4px)',
  borderRadius: '8px',
  width: isMobile ? '64px' : 'auto',
  marginRight: theme.spacing(2),
  position: 'relative',
  [theme.breakpoints.down('sm')]: {
    marginRight: theme.spacing(1.5),
  },
}));

const ScoreCircle = styled(Box)(({ theme }) => ({
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '56px',
  height: '56px',
  [theme.breakpoints.down('sm')]: {
    width: '48px',
    height: '48px',
  },
}));

const CircleBackground = styled(CircularProgress)({
  position: 'absolute',
  color: 'rgba(255, 255, 255, 0.1)',
});

const CircleProgress = styled(CircularProgress)<{ isMyth?: boolean; scoreColor?: string }>(({ theme, isMyth, scoreColor }) => ({
  position: 'absolute',
  color: scoreColor || '#4CAF50',
  '& circle': {
    strokeLinecap: 'round',
  },
}));

const ScoreText = styled(Box)({
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
});

const MythBadge = styled(Box)(({ theme }) => ({
  position: 'absolute',
  bottom: -4,
  right: 4,
  background: 'linear-gradient(135deg, #FF6B6B 0%, #FFD93D 25%, #6BCB77 50%, #4D96FF 75%, #9B72CF 100%)',
  color: '#000000',
  padding: '2px 8px',
  borderRadius: '3px',
  fontSize: '0.55rem',
  fontWeight: 600,
  display: 'flex',
  alignItems: 'center',
  zIndex: 2,
  gap: '1px',
  boxShadow: '0 1px 2px rgba(0, 0, 0, 0.2)',
  border: '1px solid rgba(255, 255, 255, 0.3)',
  '& svg': {
    fontSize: '0.6rem',
  },
}));

const DistributionContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  width: '100%',
}));

const StarContainer = styled(Box)(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  marginLeft: theme.spacing(1),
  position: 'relative',
  width: '1rem',
  height: '1rem',
}));

const StarIconStyled = styled(WorkspacePremiumIcon)<{ type: 'gold' | 'silver' | 'bronze' }>(
  ({ theme, type }) => ({
    position: 'absolute',
    fontSize: '0.9rem',
    color: type === 'gold' ? '#e6c613' : type === 'silver' ? '#C0C0C0' : '#CD7F32',
    filter: type === 'gold' ? 'drop-shadow(0 0 0px rgba(255, 215, 0, 1))' : 
           type === 'silver' ? 'drop-shadow(0 0 0px rgba(192, 192, 192, 1))' : 
           'drop-shadow(0 0 0px rgba(205, 127, 50, 1))',
    [theme.breakpoints.down('sm')]: {
      fontSize: '0.8rem',
    },
  })
);

const ScoreDistribution: React.FC<ScoreDistributionProps> = ({ distribution, rating_jp, totalCount = 0 }) => {
  const normalizedRating = rating_jp ? Math.floor((rating_jp / 20) * 100) / 100 : null;
  const progressValue = rating_jp || 0;
  
  const isMythRank = totalCount >= 50 && 
    distribution.high_rate >= 90 && 
    normalizedRating !== null && 
    normalizedRating >= 4.5;

  const totalRatings = distribution.r1 + distribution.r2 + distribution.r3;
  
  // スコアの計算（高評価: 5点、中立: 3点、低評価: 1点）
  const calculateScore = () => {
    const weightedSum = (distribution.r3 * 5) + (distribution.r2 * 3) + (distribution.r1 * 1);
    return totalRatings > 0 ? (weightedSum / totalRatings).toFixed(1) : '0.0';
  };

  const score = calculateScore();

  const getScoreColor = (score: number | null) => {
    if (!score) return '#666666';
    if (score >= 90) return '#4CAF50';
    if (score >= 80) return '#8BC34A';
    if (score >= 70) return '#CDDC39';
    if (score >= 60) return '#FFEB3B';
    if (score >= 50) return '#FFC107';
    if (score >= 41) return '#FF9800';
    return '#F44336';
  };

  const scoreColor = getScoreColor(rating_jp || null);

  const segments = [
    {
      width: distribution.high_rate,
      color: '#4caf50',
      rate: distribution.high_rate,
      count: distribution.r3,
      label: '高評価',
    },
    {
      width: distribution.neutral_rate,
      color: '#CDDC39',
      rate: distribution.neutral_rate,
      count: distribution.r2,
      label: '中立',
    },
    {
      width: distribution.low_rate,
      color: '#f44336',
      rate: distribution.low_rate,
      count: distribution.r1,
      label: '低評価',
    },
  ];

  const getStarType = (rate: number, count: number) => {
    if (count < 20) return null;
    if (rate >= 90) return 'gold';
    if (rate >= 85) return 'silver';
    if (rate >= 80) return 'bronze';
    return null;
  };

  const renderStar = (rate: number, count: number) => {
    const type = getStarType(rate, count);
    if (!type) return null;
    return (
      <StarContainer>
        <StarIconStyled type={type} />
      </StarContainer>
    );
  };

  return (
    <Box sx={{ width: '100%', mt: 1.5 }}>
      <DistributionContainer>
        <RatingContainer>
          {isMythRank && (
            <MythBadge>
              <AutoAwesomeIcon />
              <Typography variant="body2" sx={{ fontSize: '0.6rem', lineHeight: 1, fontWeight: 500, color: '#000000' }}>
                Myth
              </Typography>
            </MythBadge>
          )}
          <ScoreCircle>
            <CircleBackground
              variant="determinate"
              value={100}
              size={56}
              thickness={2}
            />
            <CircleProgress
              variant="determinate"
              value={progressValue}
              size={56}
              thickness={2}
              isMyth={isMythRank}
              scoreColor={scoreColor}
            />
            <ScoreText>
              {normalizedRating !== null ? (
                <Typography
                  variant="h4"
                  component="div"
                  sx={{
                    fontSize: {xs: '0.8rem', sm: '0.9rem'},
                    fontWeight: 'bold',
                    color: isMythRank ? '#E5E4E2' : '#ffffff',
                    lineHeight: 1
                  }}
                >
                  {normalizedRating.toFixed(2)}
                </Typography>
              ) : (
                <Typography
                  variant="h4"
                  component="div"
                  sx={{
                    fontSize: '0.8rem',
                    fontWeight: 'bold',
                    color: '#666666',
                    lineHeight: 1
                  }}
                >
                  N/A
                </Typography>
              )}
            </ScoreText>
          </ScoreCircle>
        </RatingContainer>
        <Box sx={{ flex: 1 }}>
          <DistributionBar>
            {segments.map((segment, index) => (
              <ScoreSegment
                key={index}
                width={segment.width}
                color={segment.color}
                sx={{ left: `${segments.slice(0, index).reduce((acc, s) => acc + s.width, 0)}%` }}
              />
            ))}
          </DistributionBar>
          <LegendContainer>
            {segments.map((segment, index) => (
              <LegendItem key={index}>
                <LegendLabel>
                  {segment.label}
                  {index === 0 && renderStar(distribution.high_rate, segment.count)}
                </LegendLabel>
                <LegendValue>
                  <LegendPercentage>{segment.rate.toFixed(0)}%</LegendPercentage>
                  <LegendCount>{segment.count}件</LegendCount>
                </LegendValue>
              </LegendItem>
            ))}
          </LegendContainer>
        </Box>
      </DistributionContainer>
    </Box>
  );
};

export default ScoreDistribution; 